import { useState, useEffect } from 'react';
import type { ToothData, PatientFormData } from '../types';
import VDKLegendModal from '../../../components/feature/VDKLegendModal';
import { pricingDB } from '../../../lib/pricingDB';
import { dbSurgeries } from '../../../lib/localDB';
import { isToothComplete, isPatientComplete } from '../../../shared/record';

type ToothPanelProps = {
  toothNumbers: number[];
  data: ToothData;
  patientData: PatientFormData;
  onChange: (updated: ToothData) => void;
  onRemove: () => void;
  onClose: () => void;
};

type OptionBtn = {
  value: string;
  label: string;
};

const TOOTH_FIELDS: { key: keyof ToothData; label: string; options: OptionBtn[] }[] = [
  {
    key: 'kemik',
    label: 'Kemik',
    options: [
      { value: '1', label: '1' },
      { value: '2', label: '2' },
      { value: '3', label: '3' },
    ],
  },
  {
    key: 'soket',
    label: 'Soket',
    options: [
      { value: 'e', label: 'E' },
      { value: 'h', label: 'H' },
    ],
  },
  {
    key: 'greft',
    label: 'Greft',
    options: [
      { value: 'e', label: 'E' },
      { value: 'h', label: 'H' },
    ],
  },
  {
    key: 'hacim',
    label: 'Hacim',
    options: [
      { value: 'z', label: 'Z' },
      { value: 'y', label: 'Y' },
    ],
  },
  {
    key: 'healing',
    label: 'Healing',
    options: [
      { value: 'e', label: 'E' },
      { value: 'h', label: 'H' },
    ],
  },
];

const VDK_KEYS: (keyof PatientFormData | keyof ToothData)[] = [
  'cinsiyet', 'yas', 'seker', 'sigara', 'kemik', 'soket', 'greft', 'hacim', 'healing',
];

export default function ToothPanel({
  toothNumbers,
  data,
  patientData,
  onChange,
  onRemove,
  onClose,
}: ToothPanelProps) {
  const [savedBrands, setSavedBrands] = useState<string[]>([]);

  useEffect(() => {
    dbSurgeries.getAll().then((surgeries) => {
      const brandSet = new Set<string>();
      surgeries.forEach((s) => {
        (s.teeth || []).forEach((t) => {
          if (t.implant_brand && t.implant_brand.trim()) {
            brandSet.add(t.implant_brand.trim());
          }
        });
      });
      setSavedBrands(Array.from(brandSet).sort((a, b) => a.localeCompare(b, 'tr')));
    }).catch(() => { /* ignore */ });
  }, []);

  const toothComplete = isToothComplete(data);
  const patientComplete = isPatientComplete(patientData);
  const vdkComplete = toothComplete && patientComplete;

  const pricing = pricingDB.get();
  const toothFee = toothComplete
    ? pricingDB.calcToothFee(pricing, { soket: data.soket, greft: data.greft, kemik: data.kemik, hacim: data.hacim, healing: data.healing })
    : null;

  const select = (key: keyof ToothData, value: string) => {
    onChange({ ...data, [key]: value } as ToothData);
  };

  const handleBrandInput = (val: string) => {
    onChange({ ...data, implant_brand: val });
  };

  return (
    <div className="bg-white rounded-2xl border border-teal-200 overflow-hidden animate-fadeIn">
      {/* Header */}
      <div className="bg-gradient-to-r from-teal-500 to-cyan-600 px-5 py-4 flex items-center justify-between">
        <div>
          <h4 className="text-white font-bold text-base">
            {toothNumbers.length === 1 ? `Diş #${toothNumbers[0]}` : `Dişler: ${toothNumbers.join(', ')}`}
          </h4>
          <p className="text-teal-100 text-xs mt-0.5">Parametreleri girin — Tamam ile onaylayın</p>
        </div>
      </div>

      <div className="p-5 space-y-4">
        {/* VDK Preview */}
        <div className="bg-gray-50 rounded-xl p-4">
          <div className="flex items-center justify-between mb-2">
            <p className="text-xs font-semibold text-gray-400 uppercase tracking-widest">
              Vaka Durum Kodu (VDK)
            </p>
            <VDKLegendModal variant="icon" />
          </div>
          <div className="flex items-center gap-3">
            <div className="font-mono text-2xl font-bold tracking-[0.15em] flex-1">
              {VDK_KEYS.map((key, i) => {
                const src: Record<string, string> = {
                  cinsiyet: patientData.cinsiyet,
                  yas: patientData.yas,
                  seker: patientData.seker,
                  sigara: patientData.sigara,
                  kemik: data.kemik,
                  soket: data.soket,
                  greft: data.greft,
                  hacim: data.hacim,
                  healing: data.healing,
                };
                const val = src[key as string];
                const isPatientField = ['cinsiyet', 'yas', 'seker', 'sigara'].includes(key as string);
                return (
                  <span
                    key={key as string}
                    className={
                      val
                        ? isPatientField
                          ? 'text-cyan-600'
                          : 'text-teal-600'
                        : 'text-gray-200'
                    }
                    title={isPatientField ? 'Hasta bilgisi' : 'Diş bilgisi'}
                  >
                    {val || '·'}
                  </span>
                );
              })}
            </div>
            {vdkComplete && (
              <span className="px-2.5 py-1 bg-teal-500 text-white text-xs font-bold rounded-lg whitespace-nowrap">
                Hazır
              </span>
            )}
          </div>
          <div className="flex gap-2 mt-2">
            <span className="text-xs text-gray-400 flex items-center gap-1">
              <span className="w-2 h-2 bg-cyan-400 rounded-sm inline-block"></span>
              Hasta (cinsiyet·yaş·şeker·sigara)
            </span>
            <span className="text-xs text-gray-400 flex items-center gap-1">
              <span className="w-2 h-2 bg-teal-500 rounded-sm inline-block"></span>
              Diş (kemik·soket·greft·hacim·healing)
            </span>
          </div>
          {!patientComplete && (
            <p className="text-xs text-amber-600 mt-2 flex items-center gap-1">
              <i className="ri-alert-line"></i>
              Hasta bilgilerini doldurun — siyan renkli alanlar bekleniyor
            </p>
          )}
        </div>

        {/* Tooth Fields */}
        <div className="space-y-3">
          {/* Price Badge */}
          <div className={`flex items-center justify-between rounded-xl px-4 py-3 transition-all ${
            toothFee !== null ? 'bg-teal-50 border border-teal-100' : 'bg-gray-50 border border-gray-100'
          }`}>
            <div className="flex items-center gap-2">
              <div className={`w-7 h-7 flex items-center justify-center rounded-lg ${toothFee !== null ? 'bg-teal-500' : 'bg-gray-200'}`}>
                <i className={`ri-price-tag-3-line text-sm ${toothFee !== null ? 'text-white' : 'text-gray-400'}`}></i>
              </div>
              <span className="text-sm font-semibold text-gray-700">Diş Ücreti</span>
            </div>
            <span className={`font-bold text-base font-mono ${toothFee !== null ? 'text-teal-600' : 'text-gray-400'}`}>
              {toothFee !== null
                ? `${pricing.currency}${toothFee.toLocaleString('tr-TR')}`
                : 'Tüm alanları doldurun'}
            </span>
          </div>
          {TOOTH_FIELDS.map((field) => (
            <div key={field.key} className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div
                  className={`w-5 h-5 rounded-full flex items-center justify-center text-xs font-bold transition-all ${
                    data[field.key]
                      ? 'bg-teal-500 text-white'
                      : 'bg-gray-100 text-gray-400'
                  }`}
                >
                  {data[field.key] ? (
                    <i className="ri-check-line" style={{ fontSize: '10px' }}></i>
                  ) : (
                    '·'
                  )}
                </div>
                <span className="text-sm font-semibold text-gray-700 w-14">
                  {field.label}
                </span>
              </div>
              <div className="flex gap-1.5">
                {field.options.map((opt) => {
                  const isActive = data[field.key] === opt.value;
                  return (
                    <button
                      key={opt.value}
                      type="button"
                      onClick={() => select(field.key as keyof ToothData, opt.value)}
                      className={`
                        w-10 h-10 rounded-lg text-sm font-bold transition-all cursor-pointer
                        ${isActive
                          ? 'bg-teal-500 text-white scale-105'
                          : 'bg-gray-100 text-gray-500 hover:bg-teal-50 hover:text-teal-600'
                        }
                      `}
                    >
                      {opt.label}
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
        </div>

        {/* Implant Brand */}
        <div className="border-t border-gray-100 pt-4 space-y-2">
          <div className="flex items-center gap-2">
            <div
              className={`w-5 h-5 rounded-full flex items-center justify-center text-xs font-bold transition-all ${
                data.implant_brand
                  ? 'bg-teal-500 text-white'
                  : 'bg-gray-100 text-gray-400'
              }`}
            >
              {data.implant_brand ? (
                <i className="ri-check-line" style={{ fontSize: '10px' }}></i>
              ) : (
                '·'
              )}
            </div>
            <span className="text-sm font-semibold text-gray-700">İmplant Markası</span>
            <span className="text-xs text-gray-400">(opsiyonel)</span>
          </div>
          <input
            type="text"
            value={data.implant_brand}
            onChange={(e) => handleBrandInput(e.target.value)}
            placeholder="Marka adı yazın veya seçin..."
            className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-teal-400 focus:border-transparent transition-all"
          />
          {savedBrands.length > 0 && (
            <div className="flex flex-wrap gap-1.5">
              {savedBrands.map((brand) => (
                <button
                  key={brand}
                  type="button"
                  onClick={() => handleBrandInput(data.implant_brand === brand ? '' : brand)}
                  className={`px-2.5 py-1 rounded-full text-xs font-medium transition-all cursor-pointer whitespace-nowrap ${
                    data.implant_brand === brand
                      ? 'bg-teal-500 text-white'
                      : 'bg-gray-100 text-gray-600 hover:bg-teal-50 hover:text-teal-700'
                  }`}
                >
                  {brand}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="pt-2 space-y-2">
          <button
            type="button"
            onClick={onClose}
            className="w-full py-3 bg-teal-500 text-white rounded-xl font-bold hover:bg-teal-600 transition-all cursor-pointer shadow-sm flex items-center justify-center gap-2"
          >
            <i className="ri-check-double-line text-lg"></i>
            Tamam — Sonraki Grubu Seç
          </button>
          
          <button
            type="button"
            onClick={onRemove}
            className="w-full py-2.5 text-sm text-red-500 hover:bg-red-50 rounded-xl transition-all flex items-center justify-center gap-2 cursor-pointer border border-red-100 font-medium"
          >
            <i className="ri-delete-bin-line"></i>
            {toothNumbers.length === 1 ? 'Bu dişi kaldır' : 'Seçili dişleri kaldır'}
          </button>
        </div>
      </div>
    </div>
  );
}
